var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * CZNA-1577 — „Zrušit s náhradou“ on a page of its own: both records in full, side by side.
 *
 * <p>Why a page. A supervisor decides whether two records describe the same entity by their
 * content — dates, 670, 4XX — not by their headings; NK asked for exactly that at the meeting
 * of 24. 2. 2026 (UC AR-08, step 4), and two full MARC records do not fit a modal. The
 * MergeDialog before this showed the two headings and a reference count, nothing else.
 *
 * <p>One page, two operations, because the user sees one action (CZNA-1531, F5):
 * <ul>
 *   <li>`merge` — an ACTIVE record (TASK-109): `GET …/merge/preview`, `POST …/merge` with both
 *       optimistic locks, the irreversibility checkbox, the 409/422 conflicts;</li>
 *   <li>`redirect` — a record in review (TASK-084): `GET …/references-count`,
 *       `PATCH …/review` action=redirect, no checkbox.</li>
 * </ul>
 * Each keeps its own API and its own confirm exactly as its dialog had them; what they share
 * is the header (role, heading, national control number of each side), the comparison
 * ({@link compareRecords}, CZNA-1466 — source left, target right) and the way out: Zpět to
 * where the page was opened from, success to the target's detail.
 *
 * <p>The target is picked in the existing {@link RecordPickerModal} and kept in `?target=`, so
 * a reload or a return keeps the choice. For a redirect the picker offers only same-type
 * records that are not pending review (CZNA-688): that is what makes the backend's
 * TYPE_MISMATCH, TARGET_NOT_AVAILABLE and SELF_REDIRECT unreachable from the UI.
 *
 * <p>Read-only comparison: nothing copies a field to the target (TASK-109, "Pravidlo pro UI
 * v1") — enriching the kept record stays a separate manual edit.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useStripes } from '@folio/stripes/core';
import { Button, Checkbox, Layout, Loading, MessageBanner, Pane, PaneFooter, Paneset, } from '@folio/stripes/components';
import RecordPickerModal from '../components/ProposalForm/fields/RecordPickerModal';
import RecordCompareGrid from '../components/RecordCompare';
import useRecordDetail from '../hooks/useRecordDetail';
import { useMergePreview } from '../hooks/useMergePreview';
import { useMergeRecord } from '../hooks/useMergeRecord';
import { useReviewMutation } from '../hooks/useReviewMutation';
import { useReferencesCount } from '../hooks/useReferencesCount';
import { compareRecords } from '../utils/compareRecords';
import { detailUrlFor } from '../utils/editUrlFor';
import { recordDisplayId } from '../utils/recordDisplayId';
import { canMergeRecord, canRedirectRecord } from '../utils/replaceAvailability';
import { isTimeoutError } from '../utils/errorKind';
/** Left is the record being cancelled, right the one that stays. */
var STATUS_MESSAGE_IDS = {
    same: 'ui-czna-authorities.recordReplace.compare.status.same',
    different: 'ui-czna-authorities.recordReplace.compare.status.different',
    leftOnly: 'ui-czna-authorities.recordReplace.compare.status.leftOnly',
    rightOnly: 'ui-czna-authorities.recordReplace.compare.status.rightOnly',
    ignored: 'ui-czna-authorities.recordReplace.compare.status.ignored',
};
/** The words and test hooks that differ between the two modes; everything else is shared. */
var MODE = {
    merge: {
        intro: 'ui-czna-authorities.merge.intro',
        pickTarget: 'ui-czna-authorities.merge.pickTarget',
        confirm: 'ui-czna-authorities.merge.confirm',
        cancel: 'ui-czna-authorities.merge.cancel',
        pickAttr: { 'data-test-merge-pick-target': true },
        confirmAttr: { 'data-test-merge-confirm': true },
        referencesAttr: { 'data-test-merge-preview-badge': true },
        compareAttr: { 'data-test-merge-preview': true },
    },
    redirect: {
        intro: 'ui-czna-authorities.review.redirect.intro',
        pickTarget: 'ui-czna-authorities.review.redirect.pickTarget',
        confirm: 'ui-czna-authorities.review.redirect.confirm',
        cancel: 'ui-czna-authorities.review.redirect.cancel',
        pickAttr: { 'data-test-redirect-pick-target': true },
        confirmAttr: { 'data-test-redirect-confirm': true },
        referencesAttr: { 'data-test-redirect-preview': true },
        compareAttr: { 'data-test-redirect-compare': true },
    },
};
var readErrorBody = function (error) { return __awaiter(void 0, void 0, void 0, function () {
    var resp, _a;
    var _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                resp = error === null || error === void 0 ? void 0 : error.response;
                _c.label = 1;
            case 1:
                _c.trys.push([1, 3, , 4]);
                return [4 /*yield*/, ((_b = resp === null || resp === void 0 ? void 0 : resp.json) === null || _b === void 0 ? void 0 : _b.call(resp))];
            case 2: return [2 /*return*/, _c.sent()];
            case 3:
                _a = _c.sent();
                return [2 /*return*/, undefined];
            case 4: return [2 /*return*/];
        }
    });
}); };
/** TASK-109 — the merge's stable conflict code, from a refused preview or a refused merge. */
var parseConflict = function (error) { return __awaiter(void 0, void 0, void 0, function () {
    var body;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, readErrorBody(error)];
            case 1:
                body = _a.sent();
                if (body === null || body === void 0 ? void 0 : body.code) {
                    return [2 /*return*/, {
                            code: body.code,
                            message: body.error,
                            targetFinalId: body.targetFinalId,
                            targetFinalAuthorityId: body.targetFinalAuthorityId,
                            targetFinalExportId: body.targetFinalExportId,
                        }];
                }
                return [2 /*return*/, { code: 'GENERIC' }];
        }
    });
}); };
/**
 * Only an address inside this app is a way back: `returnTo` is location state, set by the
 * detail pane that opened the page, and anything else falls back to the source's detail.
 */
var safeReturnTo = function (value) { return (typeof value === 'string' && value.startsWith('/czna-authorities/') ? value : undefined); };
var preferredHeading = function (record) { var _a, _b, _c; return (_c = (_b = (_a = record.headings) === null || _a === void 0 ? void 0 : _a.find(function (h) { return h.headingType === 'PREFERRED'; })) === null || _b === void 0 ? void 0 : _b.headingValue) !== null && _c !== void 0 ? _c : ''; };
/** One side's column heading: what it is in this operation, its heading, its control number. */
var SideHeading = function (_a) {
    var roleId = _a.roleId, record = _a.record, side = _a.side;
    var displayId = record ? recordDisplayId(record) : null;
    return (React.createElement("div", { "data-test-replace-side": side },
        React.createElement("div", { style: { fontSize: '0.75rem', fontWeight: 400, color: '#616161', textTransform: 'uppercase' } },
            React.createElement(FormattedMessage, { id: roleId })),
        React.createElement("div", { "data-test-replace-heading": true }, record ? preferredHeading(record) : '…'),
        displayId ? (React.createElement("div", { "data-test-replace-control-number": true, style: { fontWeight: 400, fontSize: '0.8rem' } }, displayId)) : null));
};
var RecordReplace = function (_a) {
    var mode = _a.mode, sectionPath = _a.sectionPath;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var stripes = useStripes();
    var id = useParams().id;
    var targetId = new URLSearchParams(location.search).get('target') || null;
    var texts = MODE[mode];
    var isMerge = mode === 'merge';
    var sourceQuery = useRecordDetail(id);
    var targetQuery = useRecordDetail(targetId !== null && targetId !== void 0 ? targetId : undefined);
    var source = sourceQuery.record;
    var target = targetId ? targetQuery.record : undefined;
    var allowed = !!source && (isMerge
        ? canMergeRecord(source, function (perm) { return stripes.hasPerm(perm); })
        : canRedirectRecord(source));
    var _b = useState(false), pickerOpen = _b[0], setPickerOpen = _b[1];
    var _c = useState(false), acknowledged = _c[0], setAcknowledged = _c[1];
    var _d = useState(null), conflict = _d[0], setConflict = _d[1];
    var _e = useState(null), previewConflict = _e[0], setPreviewConflict = _e[1];
    var _f = useState(null), redirectError = _f[0], setRedirectError = _f[1];
    // A new target is a new decision: nothing confirmed or refused about the old one carries over.
    useEffect(function () {
        setAcknowledged(false);
        setConflict(null);
        setRedirectError(null);
    }, [targetId]);
    var preview = useMergePreview(id, isMerge && allowed ? targetId : null);
    var references = useReferencesCount(id, !isMerge && allowed);
    // A preview the backend refuses (the target was merged meanwhile, another type) says why in
    // the same words a refused merge would; before CZNA-1577 it left an empty dialog.
    useEffect(function () {
        var cancelled = false;
        if (!preview.error) {
            setPreviewConflict(null);
            return undefined;
        }
        parseConflict(preview.error).then(function (parsed) {
            if (!cancelled)
                setPreviewConflict(parsed);
        });
        return function () { cancelled = true; };
    }, [preview.error]);
    var sourceType = source === null || source === void 0 ? void 0 : source.type;
    // `replace`: Back from the target's detail should not land on a confirm page of a record
    // that is gone.
    var goToTarget = function (targetRecordId) {
        if (sourceType)
            history.replace(detailUrlFor({ id: targetRecordId, type: sourceType }));
    };
    var goBack = function () { var _a, _b; return history.push((_b = safeReturnTo((_a = location.state) === null || _a === void 0 ? void 0 : _a.returnTo)) !== null && _b !== void 0 ? _b : "".concat(sectionPath, "/").concat(id)); };
    var merge = useMergeRecord(id, targetId, {
        onSuccess: function (result) { return goToTarget(result.targetRecordId); },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        // CZNA-1625 — a merge nobody answered may have happened: check before repeating it.
                        if (isTimeoutError(error)) {
                            setConflict({ code: 'GENERIC', message: intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' }) });
                            return [2 /*return*/];
                        }
                        _a = setConflict;
                        return [4 /*yield*/, parseConflict(error)];
                    case 1:
                        _a.apply(void 0, [_b.sent()]);
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var redirect = useReviewMutation(id, {
        onSuccess: function () {
            if (targetId)
                goToTarget(targetId);
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var body;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        // CZNA-1625 — same for the redirect.
                        if (isTimeoutError(error)) {
                            setRedirectError(intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' }));
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, readErrorBody(error)];
                    case 1:
                        body = _c.sent();
                        setRedirectError((_b = (_a = body === null || body === void 0 ? void 0 : body.error) !== null && _a !== void 0 ? _a : body === null || body === void 0 ? void 0 : body.code) !== null && _b !== void 0 ? _b : intl.formatMessage({ id: 'ui-czna-authorities.error.generic' }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var rows = useMemo(function () { var _a, _b; return (source && target ? compareRecords((_a = source.dataFields) !== null && _a !== void 0 ? _a : [], (_b = target.dataFields) !== null && _b !== void 0 ? _b : []) : undefined); }, [source, target]);
    var busy = merge.isLoading || redirect.isLoading;
    // AC5 — nothing is confirmed before its reference count has been shown: merge reads it from
    // its preview, redirect from references-count.
    var canConfirm = isMerge
        ? !!targetId && !!target && !!preview.data && acknowledged && !busy
        : !!targetId && !!target && !!references.data && !busy;
    var handlePick = function (pickedId) {
        setPickerOpen(false);
        history.replace({
            pathname: location.pathname,
            search: "?target=".concat(encodeURIComponent(pickedId)),
            state: location.state,
        });
    };
    var handleConfirm = function () {
        var _a, _b;
        if (!targetId || !source)
            return;
        if (isMerge) {
            if (!preview.data)
                return;
            setConflict(null);
            merge.mutate({
                targetId: targetId,
                // Prefer the source lock surfaced by the preview (fresh read); fall back to the one
                // the page loaded.
                expectedSourceOptimisticLock: (_b = (_a = preview.data.sourceOptimisticLock) !== null && _a !== void 0 ? _a : source.optimisticLock) !== null && _b !== void 0 ? _b : 0,
                expectedTargetOptimisticLock: preview.data.targetOptimisticLock,
            });
            return;
        }
        setRedirectError(null);
        redirect.mutate({
            action: 'redirect',
            targetId: targetId,
            expectedOptimisticLock: source.optimisticLock,
        });
    };
    // The data-test hooks sit on a wrapper: the e2e and unit selectors then do not depend on
    // which props a Stripes component chooses to forward.
    var renderConflict = function (shown) {
        var _a, _b, _c;
        if (shown.code === 'TARGET_ALREADY_MERGED') {
            // CZNA-977: name the surviving record by its national control number. A merge requires
            // matching types, so the whole chain shares the source's type. The UUID stays the
            // last-resort fallback.
            var authorityId = (_b = (_a = recordDisplayId({
                type: sourceType !== null && sourceType !== void 0 ? sourceType : '',
                exportId: shown.targetFinalExportId,
                authorityId: shown.targetFinalAuthorityId,
            })) !== null && _a !== void 0 ? _a : shown.targetFinalId) !== null && _b !== void 0 ? _b : '';
            return (React.createElement("div", { "data-test-merge-conflict-final": true },
                React.createElement(MessageBanner, { type: "error" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.merge.error.targetAlreadyMerged", values: { authorityId: authorityId } }))));
        }
        if (shown.code === 'TYPE_MISMATCH') {
            return (React.createElement("div", { "data-test-merge-conflict-type": true },
                React.createElement(MessageBanner, { type: "error" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.merge.error.typeMismatch" }))));
        }
        return (React.createElement("div", { "data-test-merge-conflict-generic": true },
            React.createElement(MessageBanner, { type: "error" }, (_c = shown.message) !== null && _c !== void 0 ? _c : intl.formatMessage({ id: 'ui-czna-authorities.error.generic' }))));
    };
    var referencesBanner = function () {
        var counts = isMerge
            ? preview.data && {
                rd: preview.data.referencesToRewrite.recordDataRows,
                pd: preview.data.referencesToRewrite.proposalDataRows,
            }
            : references.data && {
                rd: references.data.recordDataCount,
                pd: references.data.proposalDataCount,
            };
        if (!counts) {
            // A redirect whose count cannot be read stays unconfirmable; say why rather than leave a
            // disabled button without a reason. (A refused merge preview has its conflict banner.)
            return !isMerge && references.isError ? (React.createElement(Layout, { className: "padding-bottom-gutter", "data-test-redirect-references-error": true },
                React.createElement(MessageBanner, { type: "error" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.recordReplace.referencesError" })))) : null;
        }
        return (React.createElement(Layout, __assign({ className: "padding-bottom-gutter" }, texts.referencesAttr),
            React.createElement(MessageBanner, { type: "default" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.redirect.preview", values: counts }))));
    };
    var renderBody = function () {
        if (sourceQuery.isLoading)
            return React.createElement(Loading, null);
        if (sourceQuery.isError || !source) {
            return (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.loadError" })));
        }
        if (!allowed) {
            return (React.createElement(Layout, { "data-test-replace-not-available": true },
                React.createElement(MessageBanner, { type: "warning" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.recordReplace.notAvailable" }))));
        }
        var shownConflict = conflict !== null && conflict !== void 0 ? conflict : previewConflict;
        return (React.createElement(React.Fragment, null,
            React.createElement(Layout, { className: "padding-bottom-gutter" },
                React.createElement(FormattedMessage, { id: texts.intro, values: { heading: preferredHeading(source) } })),
            React.createElement(Layout, { className: "padding-bottom-gutter" },
                React.createElement(Button, __assign({ onClick: function () { return setPickerOpen(true); }, disabled: busy }, texts.pickAttr),
                    React.createElement(FormattedMessage, { id: targetId ? 'ui-czna-authorities.recordReplace.changeTarget' : texts.pickTarget }))),
            !targetId ? (React.createElement(MessageBanner, { type: "default" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.recordReplace.pickHint" }))) : (React.createElement(React.Fragment, null,
                referencesBanner(),
                shownConflict ? React.createElement(Layout, { className: "padding-bottom-gutter" }, renderConflict(shownConflict)) : null,
                redirectError ? (React.createElement(Layout, { className: "padding-bottom-gutter", "data-test-redirect-error": true },
                    React.createElement(MessageBanner, { type: "error" }, redirectError))) : null,
                isMerge && target && preview.data ? (React.createElement(Layout, { className: "padding-bottom-gutter" },
                    React.createElement(Checkbox, { label: intl.formatMessage({ id: 'ui-czna-authorities.merge.acknowledge' }), checked: acknowledged, onChange: function (e) { return setAcknowledged(e.target.checked); }, "data-test-merge-acknowledge": true }))) : null,
                React.createElement("div", __assign({}, texts.compareAttr),
                    React.createElement(RecordCompareGrid, { testIdPrefix: "record-replace-compare", rows: rows, statusMessageIds: STATUS_MESSAGE_IDS, differenceCountMessageId: "ui-czna-authorities.duplicateCompare.differenceCount", leftHeading: (React.createElement(SideHeading, { side: "source", roleId: "ui-czna-authorities.merge.preview.sourceLabel", record: source })), rightHeading: (React.createElement(SideHeading, { side: "target", roleId: "ui-czna-authorities.merge.preview.targetLabel", record: target })) },
                        targetQuery.isLoading ? React.createElement(Loading, null) : null,
                        targetQuery.isError ? (React.createElement(MessageBanner, { type: "error" },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.loadError" }))) : null)))),
            React.createElement(RecordPickerModal, { open: pickerOpen, onClose: function () { return setPickerOpen(false); }, recordType: source.type, 
                // CZNA-688 — a redirect target that is itself pending review is refused
                // (TARGET_NOT_AVAILABLE); APPROVED means "anything not pending" server-side.
                reviewStatus: isMerge ? undefined : 'APPROVED', onSelect: function (pickedId) { return handlePick(pickedId); } })));
    };
    var footer = allowed ? (React.createElement(PaneFooter, { renderStart: (React.createElement(Button, { onClick: goBack, disabled: busy, marginBottom0: true, "data-test-replace-cancel": true },
            React.createElement(FormattedMessage, { id: texts.cancel }))), renderEnd: (React.createElement(Button, __assign({ buttonStyle: "primary", onClick: handleConfirm, disabled: !canConfirm, marginBottom0: true }, texts.confirmAttr),
            React.createElement(FormattedMessage, { id: texts.confirm }))) })) : undefined;
    return (React.createElement(Paneset, null,
        React.createElement(Pane, { id: "record-replace-pane", defaultWidth: "fill", paneTitle: intl.formatMessage({ id: 'ui-czna-authorities.recordReplace.title' }), paneSub: source ? preferredHeading(source) : undefined, dismissible: true, onClose: goBack, footer: footer }, renderBody())));
};
export default RecordReplace;
